import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import AdminLayout from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { listAdminSettings, updateAdminSetting } from "@/lib/api";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";

const KNOWN_KEYS = [
    { key: "whatsapp_number", label: "Numéro WhatsApp (format E.164 sans +)", type: "text" },
    { key: "whatsapp_display", label: "Numéro WhatsApp affiché", type: "text" },
    { key: "brand_name", label: "Nom de la marque", type: "text" },
    { key: "brand_tagline", label: "Tagline", type: "text" },
    { key: "brand_email", label: "Email de contact", type: "email" },
];

const Settings = () => {
    const queryClient = useQueryClient();
    const { data: settings = [], isLoading } = useQuery({
        queryKey: ["admin", "settings"],
        queryFn: listAdminSettings,
    });

    const [values, setValues] = useState({});

    useEffect(() => {
        const map = {};
        settings.forEach((s) => (map[s.key] = s.value));
        setValues(map);
    }, [settings]);

    const mutation = useMutation({
        mutationFn: ({ key, value }) => updateAdminSetting(key, value),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin", "settings"] });
            queryClient.invalidateQueries({ queryKey: ["site-settings"] });
            toast.success("Paramètre mis à jour.");
        },
        onError: () => toast.error("Erreur lors de la mise à jour."),
    });

    const handleSave = (key) => {
        mutation.mutate({ key, value: values[key] });
    };

    return (
        <AdminLayout>
            <div className="space-y-6 max-w-2xl">
                <div>
                    <h1 className="font-display text-3xl font-semibold tracking-tight">
                        Paramètres
                    </h1>
                    <p className="text-neutral-500 mt-1">
                        Configurez les informations générales du site.
                    </p>
                </div>

                {isLoading ? (
                    <div className="py-12 flex justify-center">
                        <Loader2 className="w-6 h-6 animate-spin text-[#0a84ff]" />
                    </div>
                ) : (
                    <div className="bg-white rounded-xl border border-neutral-200 p-5 space-y-5">
                        {KNOWN_KEYS.map((k) => (
                            <div key={k.key} className="space-y-1.5">
                                <Label>{k.label}</Label>
                                <div className="flex items-center gap-2">
                                    <Input
                                        type={k.type}
                                        value={values[k.key] || ""}
                                        onChange={(e) =>
                                            setValues((v) => ({ ...v, [k.key]: e.target.value }))
                                        }
                                    />
                                    <Button
                                        onClick={() => handleSave(k.key)}
                                        disabled={mutation.isPending}
                                        className="shrink-0 rounded-full bg-[#1C1C1E] hover:bg-black"
                                    >
                                        {mutation.isPending ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                            <Save className="w-4 h-4" />
                                        )}
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </AdminLayout>
    );
};

export default Settings;
